import { cn } from './utils/cn';

// ─── FormSection ──────────────────────────────────────────────────────
// Outermost wrapper for a form. Stacks header + body + footer vertically.

function FormSection({ className, ...props }: React.ComponentProps<'section'>) {
  return (
    <section
      data-slot="form-section"
      className={cn('flex w-full flex-col gap-6', className)}
      {...props}
    />
  );
}

// ─── FormHeader ───────────────────────────────────────────────────────
// Title + optional description block above the form body.

function FormHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="form-header"
      className={cn('flex flex-col gap-1', className)}
      {...props}
    />
  );
}

// ─── FormTitle ────────────────────────────────────────────────────────
// The form section heading.
//   "md" (default) – 18px semibold (section headers)
//   "lg"           – 24px medium  (page-level headers)

type FormTitleSize = 'md' | 'lg';

function FormTitle({
  className,
  size = 'md',
  ...props
}: React.ComponentProps<'h2'> & { size?: FormTitleSize }) {
  return (
    <h2
      data-slot="form-title"
      className={cn(
        'text-text-primary',
        size === 'md' ? 'text-lg font-semibold' : 'text-2xl font-medium',
        className,
      )}
      {...props}
    />
  );
}

// ─── FormDescription ──────────────────────────────────────────────────
// Subtitle text below the form title.

function FormNewDescription({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="form-description"
      className={cn('text-sm font-medium text-text-tertiary', className)}
      {...props}
    />
  );
}

// ─── FormOutlinedGroup ─────────────────────────────────────────────────
// Bordered wrapper for list-style sections. Wrap a FormGroupHeader and
// a FieldGroup data-variant="list" inside this.

function FormOutlinedGroup({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="form-outlined-group"
      className={cn(
        'flex flex-col overflow-clip rounded-lg border border-border-primary',
        className,
      )}
      {...props}
    />
  );
}

// ─── FormGroupHeader ──────────────────────────────────────────────────
// Header bar for list-style forms (count text on left, action buttons on right).

function FormGroupHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="form-group-header"
      className={cn(
        'flex items-center justify-between border-b border-border-primary p-4',
        className,
      )}
      {...props}
    />
  );
}

// ─── FormGroupHeaderTitle ─────────────────────────────────────────────
// The left-side text in a group header (e.g. "3 snippets").

function FormGroupHeaderTitle({
  className,
  ...props
}: React.ComponentProps<'p'>) {
  return (
    <p
      data-slot="form-group-header-title"
      className={cn('text-sm font-medium text-text-tertiary', className)}
      {...props}
    />
  );
}

// ─── FormGroupHeaderActions ───────────────────────────────────────────
// The right-side action area in a group header.

function FormGroupHeaderActions({
  className,
  ...props
}: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="form-group-header-actions"
      className={cn('flex items-center gap-1', className)}
      {...props}
    />
  );
}

// ─── FormFooter ───────────────────────────────────────────────────────
// Right-aligned action buttons below the form body (Cancel / Save).

function FormFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="form-footer"
      className={cn('flex items-center justify-end gap-3', className)}
      {...props}
    />
  );
}

export {
  FormNewDescription,
  FormFooter,
  FormGroupHeader,
  FormGroupHeaderActions,
  FormGroupHeaderTitle,
  FormHeader,
  FormOutlinedGroup,
  FormSection,
  FormTitle,
};
